import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { createSeason, fetchSeasons } from '../../services/purchasingService';

interface SeasonPickerProps {
  value: string | null | undefined;
  onChange: (code: string) => void;
  disabled?: boolean;
}

const SEASON_RE = /^(SS|AW)\d{2}$/i;

const SeasonPicker: React.FC<SeasonPickerProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [seasons, setSeasons] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSeasons = async () => {
    try {
      const data = await fetchSeasons();
      setSeasons(data);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    loadSeasons();
  }, []);

  const handleCreate = async () => {
    const code = newCode.trim().toUpperCase();
    if (!SEASON_RE.test(code)) {
      setError(
        'Måste vara SS eller AW följt av tvåsiffrigt år (t.ex. SS26, AW27)',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createSeason(code);
      await loadSeasons();
      onChange(code);
      setDialogOpen(false);
      setNewCode('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setError(msg || 'Kunde inte skapa säsongen');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCreate();
  };

  return (
    <>
      <TextField
        select
        fullWidth
        size="small"
        label="Säsong"
        InputLabelProps={{ shrink: true }}
        value={value || ''}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === '__new__') {
            setDialogOpen(true);
          } else {
            onChange(e.target.value);
          }
        }}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">
          <em>Ingen säsong</em>
        </MenuItem>
        {seasons.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
        <MenuItem value="__new__" sx={{ color: 'primary.main' }}>
          <AddIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
          Ny säsong…
        </MenuItem>
      </TextField>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ny säsong</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Säsongskod"
            placeholder="t.ex. SS26 eller AW27"
            value={newCode}
            onChange={(e) => {
              setNewCode(e.target.value.toUpperCase());
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="SS = Vår/Sommar, AW = Höst/Vinter">
                    <span
                      style={{ fontSize: 12, color: '#888', cursor: 'help' }}
                    >
                      SS/AW
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            helperText="SS (vår/sommar) eller AW (höst/vinter) + tvåsiffrigt år"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setError(null);
              setNewCode('');
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !newCode.trim()}
          >
            Skapa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SeasonPicker;
